import React from "react";
import SectionCrudPage from "./SectionCrudPage";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import LocalContractSignDialog from "../../components/contracts/LocalContractSignDialog";
import ContractDocumentsPreviewDialog from "../../components/contracts/ContractDocumentsPreviewDialog";
import SepaMandateDialog from "../../components/contracts/SepaMandateDialog";
import SepaMandateSignDialog from "../../components/contracts/SepaMandateSignDialog";
import {
  CONTRACT_PERIODICITY_OPTIONS,
  CONTRACT_STATUS_OPTIONS,
} from "../../components/contracts/contractFormConfig";

export const createContractResources = (
  onRequestSignLocal,
  onRequestPreviewDocuments,
  onRequestSignSepa,
  onRequestSetupSepa,
  onRequestRevokeSepa,
  onRequestCollectInstallment
) => [
  {
    key: "contract-plans",
    label: "Plans",
    endpoint: "/contract-plans",
    description: "Create a plan here first. New contracts and plan versions depend on at least one plan existing.",
    relatedTabs: [
      {
        key: "contract-plan-versions",
        label: "Plan Versions",
        endpoint: "/contract-plan-versions",
        foreignKey: "plan_id",
      },
      {
        key: "contract-plan-lines",
        label: "Plan Lines",
        endpoint: "/contract-plan-lines",
        fromTab: "contract-plan-versions",
        fromField: "id",
        toField: "plan_version_id",
      },
      {
        key: "contract-plan-line-classes",
        label: "Plan Line Classes",
        endpoint: "/contract-plan-line-classes",
        fromTab: "contract-plan-lines",
        fromField: "id",
        toField: "plan_line_id",
      },
      {
        key: "contract-plan-document-rules",
        label: "Document Print Rules",
        endpoint: "/contract-plan-document-rules",
        fromTab: "contract-plan-versions",
        fromField: "id",
        toField: "plan_version_id",
      },
      {
        key: "contract-plan-signature-rules",
        label: "Signature Rules",
        endpoint: "/contract-plan-signature-rules",
        fromTab: "contract-plan-versions",
        fromField: "id",
        toField: "plan_version_id",
      },
    ],
    tableFields: [
      { name: "name", label: "Name" },
      { name: "code", label: "Code" },
      { name: "status", label: "Status" },
      { name: "currency", label: "Currency" },
    ],
    relatedTabs: [
      {
        key: "contract-plan-versions",
        label: "Plan Versions",
        endpoint: "/contract-plan-versions",
        foreignKey: "plan_id",
      },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      { name: "code", label: "Code", type: "text" },
      {
        name: "status",
        label: "Status",
        type: "select",
        defaultValue: "draft",
        options: [
          { value: "draft", label: "Draft" },
          { value: "published", label: "Published" },
          { value: "archived", label: "Archived" },
        ],
      },
      { name: "description", label: "Description", type: "text", nullable: true },
      { name: "currency", label: "Currency", type: "text", defaultValue: "EUR", placeholder: "EUR" },
    ],
  },
  {
    key: "contract-plan-versions",
    label: "Plan Versions",
    endpoint: "/contract-plan-versions",
    description: "Each plan can have one or more versions. Create a plan first if the Plan field is empty.",
    tableFields: [
      { name: "plan_id", label: "Plan", valueGetter: (row) => row?.plan?.name || row?.plan_id },
      { name: "version_number", label: "Version" },
      { name: "status", label: "Status" },
      { name: "periodicity", label: "Periodicity" },
    ],
    relatedTabs: [
      {
        key: "contract-plan-lines",
        label: "Plan Lines",
        endpoint: "/contract-plan-lines",
        foreignKey: "plan_version_id",
      },
      {
        key: "contract-plan-line-classes",
        label: "Plan Line Classes",
        endpoint: "/contract-plan-line-classes",
        fromTab: "contract-plan-lines",
        fromField: "id",
        toField: "plan_line_id",
      },
      {
        key: "contract-plan-document-rules",
        label: "Document Print Rules",
        endpoint: "/contract-plan-document-rules",
        foreignKey: "plan_version_id",
      },
    ],
    formFields: [
      { name: "plan_id", label: "Plan", type: "select", optionSource: { endpoint: "/contract-plans", valueField: "id", labelFields: ["name", "code"] } },
      { name: "version_number", label: "Version Number", type: "number", defaultValue: 1 },
      { name: "status", label: "Status", type: "select", defaultValue: "draft", options: [{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }, { value: "archived", label: "Archived" }] },
      { name: "effective_from", label: "Effective From", type: "date", nullable: true },
      { name: "effective_to", label: "Effective To", type: "date", nullable: true },
      { name: "periodicity", label: "Periodicity", type: "select", defaultValue: "monthly", options: [{ value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "yearly", label: "Yearly" }] },
      { name: "auto_renew", label: "Auto Renew", type: "boolean", defaultValue: true },
      { name: "signature_required", label: "Signature Required", type: "boolean", defaultValue: true },
      { name: "allow_direct_debit", label: "Allow Direct Debit", type: "boolean", defaultValue: true },
      { name: "notes", label: "Notes", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-plan-lines",
    label: "Plan Lines",
    endpoint: "/contract-plan-lines",
    tableFields: [
      {
        name: "plan_version_id",
        label: "Plan Version",
        valueGetter: (row) =>
          row?.version?.plan_version_label
          || (row?.version?.plan?.name
            ? `${row.version.plan.name} - v${row.version.version_number} (${row.version.status || ""})`
            : row?.plan_version_id),
      },
      { name: "product_id", label: "Product", valueGetter: (row) => row?.product?.name || row?.product_id },
      { name: "unit_price", label: "Unit Price" },
      { name: "installments_count", label: "Installments" },
    ],
    relatedTabs: [
      {
        key: "contract-plan-line-classes",
        label: "Allowed Classes",
        endpoint: "/contract-plan-line-classes",
        foreignKey: "plan_line_id",
      },
    ],
    formFields: [
      { name: "plan_version_id", label: "Plan Version", type: "select", optionSource: { endpoint: "/contract-plan-versions", valueField: "id", labelFields: ["plan_version_label"] } },
      { name: "product_id", label: "Product", type: "select", optionSource: { endpoint: "/products", valueField: "id", labelFields: ["name", "code"] } },
      { name: "line_order", label: "Line Order", type: "number" },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "unit_price", label: "Unit Price", type: "number" },
      { name: "discount_type", label: "Discount Type", type: "select", options: [{ value: "none", label: "None" }, { value: "percent", label: "Percent" }, { value: "fixed", label: "Fixed" }] },
      { name: "discount_value", label: "Discount Value", type: "number", nullable: true },
      { name: "vat_rate_id", label: "VAT Rate", type: "select", nullable: true, optionSource: { endpoint: "/vat-rates", valueField: "id", labelFields: ["name", "code", "rate"] } },
      { name: "billing_start_rule", label: "Billing Start Rule", type: "select", options: [{ value: "contract_start", label: "Contract Start" }, { value: "next_cycle_start", label: "Next Cycle Start" }] },
      { name: "billing_end_rule", label: "Billing End Rule", type: "select", options: [{ value: "open", label: "Open" }, { value: "fixed_term", label: "Fixed Term" }] },
      { name: "term_cycles", label: "Term Cycles", type: "number", nullable: true },
      { name: "entry_policy", label: "Entry Policy", type: "select", options: [{ value: "first_payment_only", label: "First Payment Only" }, { value: "all_payments", label: "All Payments" }, { value: "none", label: "None" }] },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: [{ value: "onsite", label: "Onsite" }, { value: "post_dated_checks", label: "Post-Dated Checks" }, { value: "bank_transfer_first_installment", label: "Bank Transfer (First Installment)" }, { value: "bank_transfer_full", label: "Bank Transfer (Full)" }, { value: "direct_debit", label: "Direct Debit" }] },
      { name: "installments_count", label: "Installments Count", type: "number" },
      { name: "collector_user_id", label: "Collector", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "commission_user_id", label: "Commission User", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "notes", label: "Notes", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-plan-line-classes",
    label: "Plan Line Classes",
    endpoint: "/contract-plan-line-classes",
    tableFields: [
      {
        name: "plan_line_id",
        label: "Plan Line",
        valueGetter: (row) =>
          row?.plan_line_label
          || row?.planLine?.plan_line_label
          || row?.plan_line?.plan_line_label
          || row?.plan_line?.line_order
          || row?.planLine?.line_order
          || row?.plan_line_id,
      },
      {
        name: "class_id",
        label: "Gym Class",
        valueGetter: (row) =>
          row?.gym_class_label
          || row?.gym_class?.name
          || row?.gymClass?.name
          || row?.class_id,
      },
      { name: "status", label: "Status" },
      { name: "valid_from", label: "Valid From" },
      { name: "valid_to", label: "Valid To" },
      { name: "keep_until", label: "Keep Until" },
    ],
    formFields: [
      { name: "plan_line_id", label: "Plan Line", type: "select", optionSource: { endpoint: "/contract-plan-lines", valueField: "id", labelFields: ["plan_line_label"] } },
      { name: "class_id", label: "Gym Class", type: "select", optionSource: { endpoint: "/gym-classes", valueField: "id", labelFields: ["name", "code", "id"] } },
      { name: "status", label: "Status", type: "select", options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
      { name: "valid_from", label: "Valid From", type: "date", nullable: true },
      { name: "valid_to", label: "Valid To", type: "date", nullable: true },
      { name: "keep_until", label: "Keep Until", type: "date", nullable: true },
    ],
  },
  {
    key: "contract-plan-document-rules",
    label: "Document Print Rules",
    endpoint: "/contract-plan-document-rules",
    tableFields: [
      {
        name: "plan_version_id",
        label: "Plan Version",
        valueGetter: (row) =>
          row?.version?.plan_version_label
          || (row?.version?.plan?.name
            ? `${row.version.plan.name} - v${row.version.version_number}`
            : row?.plan_version_id),
      },
      { name: "document_type", label: "Document" },
      {
        name: "is_required",
        label: "Enable Print",
        valueGetter: (row) => (row?.is_required ? "Yes" : "No"),
      },
    ],
    formFields: [
      { name: "plan_version_id", label: "Plan Version", type: "select", optionSource: { endpoint: "/contract-plan-versions", valueField: "id", labelFields: ["plan_version_label"] } },
      {
        name: "document_type",
        label: "Document",
        type: "select",
        options: [
          { value: "liability_term", label: "Termo de Responsabilidade" },
          { value: "sepa_mandate", label: "SEPA (Autorizacao Debito Direto)" },
          { value: "gdpr", label: "RGPD" },
          { value: "image_authorization", label: "Autorizacao de Imagens Redes Sociais" },
          { value: "cancellation_form", label: "Ficha de Cancelamento" },
          { value: "suspension_form", label: "Ficha de Suspensao" },
        ],
      },
      {
        name: "is_required",
        label: "Enable Print",
        type: "radio",
        options: [
          { value: true, label: "Yes" },
          { value: false, label: "No" },
        ],
      },
    ],
    toPayload: (formData) => ({
      ...formData,
      is_required: String(formData?.is_required) === "true" || formData?.is_required === true,
    }),
  },
  {
    key: "contract-plan-signature-rules",
    label: "Signature Rules",
    endpoint: "/contract-plan-signature-rules",
    tableFields: [
      {
        name: "plan_version_id",
        label: "Plan Version",
        valueGetter: (row) =>
          row?.version?.plan_version_label
          || (row?.version?.plan?.name
            ? `${row.version.plan.name} - v${row.version.version_number}`
            : row?.plan_version_id),
      },
      { name: "signature_provider", label: "Provider" },
      {
        name: "is_required",
        label: "Required",
        valueGetter: (row) => (row?.is_required ? "Yes" : "No"),
      },
      {
        name: "sign_before_activation",
        label: "Before Activation",
        valueGetter: (row) => (row?.sign_before_activation ? "Yes" : "No"),
      },
    ],
    formFields: [
      { name: "plan_version_id", label: "Plan Version", type: "select", optionSource: { endpoint: "/contract-plan-versions", valueField: "id", labelFields: ["plan_version_label"] } },
      { name: "signature_provider", label: "Provider", type: "text", nullable: true, placeholder: "local_pad" },
      { name: "is_required", label: "Required", type: "boolean", defaultValue: true },
      { name: "sign_before_activation", label: "Sign Before Activation", type: "boolean", defaultValue: true },
    ],
  },
  {
    key: "contracts",
    label: "Contracts",
    endpoint: "/contracts",
    actionsColumnWidth: 300,
    description: "If the Plan field is empty, create a plan in the Plans tab first.",
    tableFields: [
      { name: "contract_number", label: "Contract Number" },
      { name: "user_id", label: "User", valueGetter: (row) => row?.user?.name || row?.user?.email || row?.user_id },
      { name: "plan_id", label: "Plan", valueGetter: (row) => row?.plan?.name || row?.plan_id },
      { name: "status", label: "Status" },
      { name: "mandate_status", label: "Mandate Status" },
      { name: "mandate_reference", label: "Mandate Reference" },
      { name: "start_date", label: "Start Date" },
      { name: "contract_total", label: "Price" },
      { name: "periodicity", label: "Periodicity" },
    ],
    rowActions: ({ row, reload }) => {
      const rowId = row?.id;
      if (!rowId) return [];
      const alreadySigned = Boolean(row?.signature_date);
      const actions = [];

      if (typeof onRequestPreviewDocuments === "function") {
        actions.push({
          label: "Preview Docs",
          icon: "view",
          color: "info",
          onClick: () => onRequestPreviewDocuments({ row }),
        });
      }

      if (typeof onRequestSetupSepa === "function") {
        actions.push({
          label: "Setup SEPA",
          icon: "edit",
          color: "primary",
          onClick: () => onRequestSetupSepa({ row, reload }),
        });
      }

      if (row?.mandate_status === "active" && typeof onRequestRevokeSepa === "function") {
        actions.push({
          label: "Revoke SEPA",
          icon: "delete",
          color: "error",
          onClick: () => onRequestRevokeSepa({ row, reload }),
        });
      }

      if (!alreadySigned && typeof onRequestSignLocal === "function") {
        actions.push({
          label: "Sign Local",
          icon: "edit",
          color: "success",
          onClick: () => onRequestSignLocal({ row, reload }),
        });
      }

      return actions;
    },
    relatedTabs: [
      { key: "contract-lines", label: "Lines", endpoint: "/contract-lines", foreignKey: "contract_id" },
      { key: "contract-payers", label: "Payers", endpoint: "/contract-payers", fromTab: "contract-lines", fromField: "id", toField: "contract_line_id" },
      { key: "contract-line-classes", label: "Contract Classes", endpoint: "/contract-line-classes", fromTab: "contract-lines", fromField: "id", toField: "contract_line_id" },
      { key: "contract-installments", label: "Installments", endpoint: "/contract-installments", foreignKey: "contract_id" },
      { key: "contract-installment-payments", label: "Installment Payments", endpoint: "/contract-installment-payments", fromTab: "contract-installments", fromField: "id", toField: "contract_installment_id" },
      { key: "contract-debts", label: "Debts", endpoint: "/contract-debts", foreignKey: "contract_id" },
      { key: "contract-documents", label: "Documents", endpoint: "/contract-documents", foreignKey: "contract_id" },
      { key: "contract-signatures", label: "Signatures", endpoint: "/contract-signatures", foreignKey: "contract_id" },
      { key: "sepa-mandates", label: "SEPA Mandates", endpoint: "/sepa-mandates", foreignKey: "contract_id" },
      { key: "contract-events", label: "Timeline", endpoint: "/contract-events", foreignKey: "contract_id", readOnly: true },
    ],
    formFields: [
      { name: "contract_number", label: "Contract Number", type: "text" },
      { name: "user_id", label: "User", type: "select", optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "plan_id", label: "Plan", type: "select", optionSource: { endpoint: "/contract-plans", valueField: "id", labelFields: ["name", "code"] } },
      { name: "plan_version_id", label: "Plan Version", type: "select", optionSource: { endpoint: "/contract-plan-versions", valueField: "id", labelFields: ["plan_version_label"] } },
      { name: "status", label: "Status", type: "select", options: CONTRACT_STATUS_OPTIONS },
      { name: "currency", label: "Currency", type: "text", defaultValue: "EUR", placeholder: "EUR" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "signature_date", label: "Signature Date", type: "date", nullable: true },
      { name: "renewal_date", label: "Renewal Date", type: "date", nullable: true },
      { name: "periodicity", label: "Periodicity", type: "select", options: CONTRACT_PERIODICITY_OPTIONS },
      { name: "auto_renew", label: "Auto Renew", type: "boolean" },
      { name: "notes", label: "Notes", type: "text", nullable: true },
      { name: "internal_note", label: "Internal Note", type: "text", nullable: true },
      { name: "activated_at", label: "Activated At", type: "datetime", nullable: true },
      { name: "terminated_at", label: "Terminated At", type: "datetime", nullable: true },
    ],
  },
  {
    key: "contract-lines",
    label: "Contract Lines",
    endpoint: "/contract-lines",
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "product_id", label: "Product", valueGetter: (row) => row?.product?.name || row?.product_id },
      { name: "line_total", label: "Line Total" },
      { name: "payment_mode", label: "Payment Mode" },
    ],
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number", "status"] } },
      { name: "source_plan_line_id", label: "Source Plan Line", type: "select", nullable: true, optionSource: { endpoint: "/contract-plan-lines", valueField: "id", labelFields: ["plan_line_label"] } },
      { name: "line_order", label: "Line Order", type: "number" },
      { name: "product_id", label: "Product", type: "select", optionSource: { endpoint: "/products", valueField: "id", labelFields: ["name", "code"] } },
      { name: "quantity", label: "Quantity", type: "number" },
      { name: "unit_price", label: "Unit Price", type: "number" },
      { name: "discount_type", label: "Discount Type", type: "select", options: [{ value: "none", label: "None" }, { value: "percent", label: "Percent" }, { value: "fixed", label: "Fixed" }] },
      { name: "discount_value", label: "Discount Value", type: "number", nullable: true },
      { name: "vat_rate_id", label: "VAT Rate", type: "select", nullable: true, optionSource: { endpoint: "/vat-rates", valueField: "id", labelFields: ["name", "code"] } },
      { name: "line_subtotal", label: "Line Subtotal", type: "number" },
      { name: "line_tax_total", label: "Line Tax Total", type: "number" },
      { name: "line_total", label: "Line Total", type: "number" },
      { name: "valid_from", label: "Valid From", type: "date", nullable: true },
      { name: "valid_to", label: "Valid To", type: "date", nullable: true },
      { name: "entry_policy", label: "Entry Policy", type: "select", options: [{ value: "first_payment_only", label: "First Payment Only" }, { value: "all_payments", label: "All Payments" }, { value: "none", label: "None" }] },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: [{ value: "onsite", label: "Onsite" }, { value: "post_dated_checks", label: "Post-Dated Checks" }, { value: "bank_transfer_first_installment", label: "Bank Transfer (First Installment)" }, { value: "bank_transfer_full", label: "Bank Transfer (Full)" }, { value: "direct_debit", label: "Direct Debit" }] },
      { name: "installments_count", label: "Installments", type: "number" },
      { name: "collector_user_id", label: "Collector", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "commission_user_id", label: "Commission User", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "notes", label: "Notes", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-payers",
    label: "Contract Payers",
    endpoint: "/contract-payers",
    tableFields: [
      { name: "contract_line_id", label: "Line", valueGetter: (row) => row?.line?.line_order || row?.contract_line_id },
      { name: "payer_user_id", label: "Payer", valueGetter: (row) => row?.payer?.name || row?.payer?.email || row?.payer_user_id },
      { name: "allocation_type", label: "Allocation Type" },
      { name: "allocation_value", label: "Allocation Value" },
      { name: "authorization_status", label: "Authorization" },
    ],
    formFields: [
      { name: "contract_line_id", label: "Contract Line", type: "select", optionSource: { endpoint: "/contract-lines", valueField: "id", labelFields: ["id", "line_order"] } },
      { name: "payer_user_id", label: "Payer", type: "select", optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "allocation_type", label: "Allocation Type", type: "select", options: [{ value: "percent", label: "Percent" }, { value: "fixed", label: "Fixed" }] },
      { name: "allocation_value", label: "Allocation Value", type: "number" },
      { name: "iban", label: "IBAN", type: "text", nullable: true },
      { name: "sepa_mandate_id", label: "SEPA Mandate", type: "select", nullable: true, optionSource: { endpoint: "/sepa-mandates", valueField: "id", labelFields: ["mandate_reference", "status"] } },
      { name: "authorization_status", label: "Authorization Status", type: "select", options: [{ value: "pending", label: "Pending" }, { value: "authorized", label: "Authorized" }, { value: "revoked", label: "Revoked" }] },
      { name: "authorized_at", label: "Authorized At", type: "datetime", nullable: true },
    ],
  },
  {
    key: "contract-line-classes",
    label: "Contract Classes",
    endpoint: "/contract-line-classes",
    tableFields: [
      { name: "contract_line_id", label: "Contract Line", valueGetter: (row) => row?.line?.line_order || row?.contract_line_id },
      { name: "class_id", label: "Gym Class", valueGetter: (row) => row?.gym_class?.name || row?.gymClass?.name || row?.class_id },
      { name: "status", label: "Status" },
      { name: "valid_from", label: "Valid From" },
      { name: "valid_to", label: "Valid To" },
      { name: "keep_until", label: "Keep Until" },
    ],
    formFields: [
      { name: "contract_line_id", label: "Contract Line", type: "select", optionSource: { endpoint: "/contract-lines", valueField: "id", labelFields: ["id", "line_order"] } },
      { name: "class_id", label: "Gym Class", type: "select", optionSource: { endpoint: "/gym-classes", valueField: "id", labelFields: ["name", "code"] } },
      { name: "status", label: "Status", type: "select", options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
      { name: "valid_from", label: "Valid From", type: "date", nullable: true },
      { name: "valid_to", label: "Valid To", type: "date", nullable: true },
      { name: "keep_until", label: "Keep Until", type: "date", nullable: true },
    ],
  },
  {
    key: "contract-installments",
    label: "Installments",
    endpoint: "/contract-installments",
    actionsColumnWidth: 180,
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "installment_number", label: "Number" },
      { name: "due_date", label: "Due Date" },
      { name: "collection_date", label: "Collection Date" },
      { name: "expected_amount", label: "Expected" },
      { name: "paid_amount", label: "Paid" },
      { name: "outstanding_amount", label: "Outstanding" },
      { name: "status", label: "Status" },
      { name: "submitted_at", label: "Submitted At" },
      { name: "paid_at", label: "Paid At" },
      { name: "failed_at", label: "Failed At" },
      { name: "failure_code", label: "Failure Code" },
    ],
    rowActions: ({ row, reload }) => {
      if (!row?.id || typeof onRequestCollectInstallment !== "function") return [];
      if (row?.status === "paid") return [];
      if (row?.contract?.mandate_status !== "active") return [];

      return [
        {
          label: "Collect Now",
          icon: "download",
          color: "success",
          onClick: () => onRequestCollectInstallment({ row, reload }),
        },
      ];
    },
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number", "status"] } },
      { name: "installment_number", label: "Installment Number", type: "number" },
      { name: "due_date", label: "Due Date", type: "date" },
      { name: "collection_date", label: "Collection Date", type: "date", nullable: true },
      { name: "period_start", label: "Period Start", type: "date", nullable: true },
      { name: "period_end", label: "Period End", type: "date", nullable: true },
      { name: "expected_amount", label: "Expected Amount", type: "number" },
      { name: "paid_amount", label: "Paid Amount", type: "number" },
      { name: "outstanding_amount", label: "Outstanding Amount", type: "number" },
      { name: "status", label: "Status", type: "select", options: [{ value: "pending", label: "Pending" }, { value: "partial", label: "Partial" }, { value: "paid", label: "Paid" }, { value: "overdue", label: "Overdue" }, { value: "cancelled", label: "Cancelled" }] },
      { name: "last_payment_at", label: "Last Payment At", type: "datetime", nullable: true },
      { name: "provider_payment_intent_id", label: "Provider Payment Intent", type: "text", nullable: true },
      { name: "provider_charge_id", label: "Provider Charge", type: "text", nullable: true },
      { name: "submitted_at", label: "Submitted At", type: "datetime", nullable: true },
      { name: "paid_at", label: "Paid At", type: "datetime", nullable: true },
      { name: "failed_at", label: "Failed At", type: "datetime", nullable: true },
      { name: "returned_at", label: "Returned At", type: "datetime", nullable: true },
      { name: "failure_code", label: "Failure Code", type: "text", nullable: true },
      { name: "failure_reason", label: "Failure Reason", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-installment-payments",
    label: "Installment Payments",
    endpoint: "/contract-installment-payments",
    tableFields: [
      { name: "contract_installment_id", label: "Installment", valueGetter: (row) => row?.installment?.installment_number || row?.contract_installment_id },
      { name: "payment_method_id", label: "Payment Method", valueGetter: (row) => row?.payment_method?.name || row?.paymentMethod?.name || row?.payment_method_id },
      { name: "payment_mode", label: "Payment Mode" },
      { name: "amount", label: "Amount" },
      { name: "paid_at", label: "Paid At" },
    ],
    formFields: [
      { name: "contract_installment_id", label: "Installment", type: "select", optionSource: { endpoint: "/contract-installments", valueField: "id", labelFields: ["installment_number", "due_date"] } },
      { name: "payment_method_id", label: "Payment Method", type: "select", nullable: true, optionSource: { endpoint: "/payment-methods", valueField: "id", labelFields: ["name"] } },
      { name: "payment_mode", label: "Payment Mode", type: "select", options: [{ value: "onsite", label: "Onsite" }, { value: "bank_transfer", label: "Bank Transfer" }, { value: "direct_debit", label: "Direct Debit" }, { value: "check", label: "Check" }, { value: "other", label: "Other" }] },
      { name: "amount", label: "Amount", type: "number" },
      { name: "paid_at", label: "Paid At", type: "datetime" },
      { name: "reference", label: "Reference", type: "text", nullable: true },
      { name: "processed_by_user_id", label: "Processed By", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "sale_payment_id", label: "Sale Payment", type: "select", nullable: true, optionSource: { endpoint: "/sale-payments", valueField: "id", labelFields: ["id", "method", "paid_at"] } },
    ],
  },
  {
    key: "contract-debts",
    label: "Debts",
    endpoint: "/contract-debts",
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "status", label: "Status" },
      { name: "total_amount", label: "Total" },
      { name: "paid_amount", label: "Paid" },
      { name: "outstanding_amount", label: "Outstanding" },
      { name: "opened_at", label: "Opened At" },
    ],
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number"] } },
      { name: "contract_installment_id", label: "Installment", type: "select", nullable: true, optionSource: { endpoint: "/contract-installments", valueField: "id", labelFields: ["installment_number", "due_date"] } },
      { name: "principal_amount", label: "Principal Amount", type: "number" },
      { name: "extra_charge_amount", label: "Extra Charge", type: "number", nullable: true },
      { name: "total_amount", label: "Total Amount", type: "number" },
      { name: "paid_amount", label: "Paid Amount", type: "number" },
      { name: "outstanding_amount", label: "Outstanding Amount", type: "number" },
      { name: "status", label: "Status", type: "select", options: [{ value: "open", label: "Open" }, { value: "partial", label: "Partial" }, { value: "paid", label: "Paid" }, { value: "waived", label: "Waived" }] },
      { name: "opened_at", label: "Opened At", type: "datetime" },
      { name: "closed_at", label: "Closed At", type: "datetime", nullable: true },
      { name: "waived_by_user_id", label: "Waived By", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "waive_reason", label: "Waive Reason", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-documents",
    label: "Documents",
    endpoint: "/contract-documents",
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "document_type", label: "Type" },
      { name: "file_name", label: "File Name" },
      { name: "status", label: "Status" },
      { name: "version", label: "Version" },
      { name: "uploaded_by_user_id", label: "Uploaded By", valueGetter: (row) => row?.uploadedBy?.name || row?.uploadedBy?.email || row?.uploaded_by_user_id },
    ],
    rowActions: ({ row, openBinaryDocument }) => {
      const rowId = row?.id;
      if (!rowId) return [];

      return [
        {
          label: "View",
          icon: "view",
          color: "info",
          onClick: () => openBinaryDocument(`/contract-documents/${rowId}/view`, row?.file_name, "inline"),
        },
        {
          label: "Download",
          icon: "download",
          color: "primary",
          onClick: () => openBinaryDocument(`/contract-documents/${rowId}/download`, row?.file_name, "download"),
        },
      ];
    },
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number"] } },
      { name: "document_type", label: "Document Type", type: "select", options: [{ value: "sepa_mandate", label: "SEPA Mandate" }, { value: "id_document", label: "ID Document" }, { value: "contract_terms", label: "Contract Terms" }, { value: "signature_evidence", label: "Signature Evidence" }, { value: "other", label: "Other" }] },
      { name: "file", label: "Upload / Replace File", type: "file" },
      { name: "status", label: "Status", type: "select", defaultValue: "uploaded", options: [{ value: "uploaded", label: "Uploaded" }, { value: "validated", label: "Validated" }, { value: "rejected", label: "Rejected" }, { value: "expired", label: "Expired" }] },
      { name: "validated_by_user_id", label: "Validated By", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "validated_at", label: "Validated At", type: "datetime", nullable: true },
      { name: "notes", label: "Notes", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-signatures",
    label: "Signatures",
    endpoint: "/contract-signatures",
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "provider", label: "Provider" },
      { name: "status", label: "Status" },
      { name: "signer_user_id", label: "Signer", valueGetter: (row) => row?.signer?.name || row?.signer?.email || row?.signer_user_id },
      { name: "signed_at", label: "Signed At" },
    ],
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number"] } },
      { name: "provider", label: "Provider", type: "text" },
      { name: "provider_request_id", label: "Provider Request ID", type: "text", nullable: true },
      { name: "provider_envelope_id", label: "Provider Envelope ID", type: "text", nullable: true },
      { name: "status", label: "Status", type: "select", options: [{ value: "pending", label: "Pending" }, { value: "sent", label: "Sent" }, { value: "signed", label: "Signed" }, { value: "declined", label: "Declined" }, { value: "expired", label: "Expired" }, { value: "failed", label: "Failed" }] },
      { name: "signer_user_id", label: "Signer", type: "select", optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "signer_email", label: "Signer Email", type: "text", nullable: true },
      { name: "sent_at", label: "Sent At", type: "datetime", nullable: true },
      { name: "signed_at", label: "Signed At", type: "datetime", nullable: true },
      {
        name: "evidence_document_id",
        label: "Evidence Document",
        type: "text",
        nullable: true,
        disabled: true,
        readOnly: true,
        previewDocument: { endpointTemplate: "/contract-documents/{id}/view", mode: "inline", label: "Preview" },
      },
      { name: "webhook_payload", label: "Webhook Payload", type: "text", nullable: true, formatJson: true, renderAsTable: true, readOnly: true },
    ],
    toPayload: (formData) => {
      const payload = { ...formData };
      if (typeof payload.webhook_payload === "string" && payload.webhook_payload.trim() !== "") {
        try {
          payload.webhook_payload = JSON.parse(payload.webhook_payload);
        } catch (_) {
          payload.webhook_payload = {};
        }
      }
      return payload;
    },
  },
  {
    key: "sepa-mandates",
    label: "SEPA Mandates",
    endpoint: "/sepa-mandates",
    actionsColumnWidth: 300,
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "payer_user_id", label: "Payer", valueGetter: (row) => row?.payer?.name || row?.payer?.email || row?.payer_user_id },
      { name: "mandate_reference", label: "Mandate Ref" },
      { name: "status", label: "Status" },
      { name: "valid_from", label: "Valid From" },
      { name: "valid_to", label: "Valid To" },
    ],
    rowActions: ({ row, reload, openBinaryDocument }) => {
      const actions = [];

      if (row?.document_id) {
        actions.push({
          label: "View Mandate",
          icon: "view",
          color: "info",
          onClick: () => openBinaryDocument(`/contract-documents/${row.document_id}/view`, `sepa_${row?.mandate_reference || "mandate"}.pdf`, "inline"),
        });
      }

      if (typeof onRequestSignSepa === "function" && !["revoked", "expired"].includes(String(row?.status || ""))) {
        actions.push({
          label: "Sign SEPA",
          icon: "edit",
          color: "success",
          onClick: () => onRequestSignSepa({ row, reload }),
        });
      }

      if (typeof onRequestSetupSepa === "function") {
        actions.push({
          label: "Setup SEPA",
          icon: "edit",
          color: "primary",
          onClick: () => onRequestSetupSepa({ row, reload }),
        });
      }

      if (row?.status === "active" && typeof onRequestRevokeSepa === "function") {
        actions.push({
          label: "Revoke SEPA",
          icon: "delete",
          color: "error",
          onClick: () => onRequestRevokeSepa({ row, reload }),
        });
      }

      return actions;
    },
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number"] } },
      { name: "payer_user_id", label: "Payer", type: "select", optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "mandate_reference", label: "Mandate Reference", type: "text" },
      { name: "iban", label: "IBAN", type: "text" },
      { name: "account_holder_name", label: "Account Holder Name", type: "text" },
      { name: "signed_on", label: "Signed On", type: "date", nullable: true },
      { name: "valid_from", label: "Valid From", type: "date", nullable: true },
      { name: "valid_to", label: "Valid To", type: "date", nullable: true },
      { name: "status", label: "Status", type: "select", defaultValue: "draft", options: [{ value: "draft", label: "Draft" }, { value: "active", label: "Active" }, { value: "revoked", label: "Revoked" }, { value: "expired", label: "Expired" }] },
      {
        name: "document_id",
        label: "Document",
        type: "select",
        nullable: true,
        optionSource: { endpoint: "/contract-documents", valueField: "id", labelFields: ["file_name", "status"] },
        previewDocument: { endpointTemplate: "/contract-documents/{id}/view", mode: "inline", label: "Preview" },
      },
      { name: "revoked_at", label: "Revoked At", type: "datetime", nullable: true },
      { name: "revoke_reason", label: "Revoke Reason", type: "text", nullable: true },
    ],
  },
  {
    key: "contract-events",
    label: "Events",
    endpoint: "/contract-events",
    tableFields: [
      { name: "contract_id", label: "Contract", valueGetter: (row) => row?.contract?.contract_number || row?.contract_id },
      { name: "event_type", label: "Event Type" },
      { name: "event_at", label: "Event At" },
      { name: "actor_user_id", label: "Actor", valueGetter: (row) => row?.actor?.name || row?.actor?.email || row?.actor_user_id },
    ],
    formFields: [
      { name: "contract_id", label: "Contract", type: "select", optionSource: { endpoint: "/contracts", valueField: "id", labelFields: ["contract_number"] } },
      {
        name: "event_type",
        label: "Event Type",
        type: "select",
        options: [
          { value: "generated", label: "Generated" },
          { value: "activated", label: "Activated" },
          { value: "payment_posted", label: "Payment Posted" },
          { value: "debt_opened", label: "Debt Opened" },
          { value: "debt_closed", label: "Debt Closed" },
          { value: "settled", label: "Settled" },
          { value: "suspended", label: "Suspended" },
          { value: "terminated", label: "Terminated" },
          { value: "cancelled", label: "Cancelled" },
          { value: "document_uploaded", label: "Document Uploaded" },
          { value: "signature_requested", label: "Signature Requested" },
          { value: "signature_signed", label: "Signature Signed" },
          { value: "sepa_activated", label: "SEPA Activated" },
          { value: "sepa_revoked", label: "SEPA Revoked" },
          { value: "mandate_created", label: "Mandate Created" },
          { value: "mandate_confirmed", label: "Mandate Confirmed" },
          { value: "mandate_revoked", label: "Mandate Revoked" },
          { value: "collection_submitted", label: "Collection Submitted" },
          { value: "collection_paid", label: "Collection Paid" },
          { value: "collection_failed", label: "Collection Failed" },
          { value: "collection_returned", label: "Collection Returned" },
        ],
      },
      { name: "event_at", label: "Event At", type: "datetime" },
      { name: "actor_user_id", label: "Actor", type: "select", nullable: true, optionSource: { endpoint: "/users", valueField: "id", labelFields: ["name", "email"] } },
      { name: "payload", label: "Payload JSON", type: "text", nullable: true },
    ],
    toPayload: (formData) => {
      const payload = { ...formData };
      if (typeof payload.payload === "string" && payload.payload.trim() !== "") {
        try {
          payload.payload = JSON.parse(payload.payload);
        } catch (_) {
          payload.payload = {};
        }
      }
      return payload;
    },
  },
];

export const contractResources = createContractResources();

export default function ContractsPage() {
  const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false);
  const [previewTargetRow, setPreviewTargetRow] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");
  const [previewContractUrl, setPreviewContractUrl] = React.useState("");
  const [previewSepaUrl, setPreviewSepaUrl] = React.useState("");
  const [previewAdditionalPreviewDocs, setPreviewAdditionalPreviewDocs] = React.useState([]);
  const [previewType, setPreviewType] = React.useState("contract");
  const [sepaDialogOpen, setSepaDialogOpen] = React.useState(false);
  const [sepaDialogMode, setSepaDialogMode] = React.useState("setup");
  const [sepaTargetRow, setSepaTargetRow] = React.useState(null);
  const [sepaReload, setSepaReload] = React.useState(null);
  const [sepaSubmitting, setSepaSubmitting] = React.useState(false);
  const [sepaError, setSepaError] = React.useState("");
  const [sepaForm, setSepaForm] = React.useState({
    iban: "",
    account_holder_name: "",
    email: "",
    reason: "",
  });

  const [signDialogOpen, setSignDialogOpen] = React.useState(false);
  const [signTargetRow, setSignTargetRow] = React.useState(null);
  const [signReload, setSignReload] = React.useState(null);
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);
  const [signSepa, setSignSepa] = React.useState(true);
  const [signError, setSignError] = React.useState("");
  const [signSubmitting, setSignSubmitting] = React.useState(false);
  const [signPreviewLoading, setSignPreviewLoading] = React.useState(false);
  const [signPreviewError, setSignPreviewError] = React.useState("");
  const [signContractPreviewUrl, setSignContractPreviewUrl] = React.useState("");
  const [signSepaPreviewUrl, setSignSepaPreviewUrl] = React.useState("");
  const [signAdditionalPreviewDocs, setSignAdditionalPreviewDocs] = React.useState([]);
  const [signPreviewType, setSignPreviewType] = React.useState("contract");

  const [sepaSignDialogOpen, setSepaSignDialogOpen] = React.useState(false);
  const [sepaSignTargetRow, setSepaSignTargetRow] = React.useState(null);
  const [sepaSignReload, setSepaSignReload] = React.useState(null);
  const [sepaSignAcceptedTerms, setSepaSignAcceptedTerms] = React.useState(false);
  const [sepaSignError, setSepaSignError] = React.useState("");
  const [sepaSignSubmitting, setSepaSignSubmitting] = React.useState(false);
  const [sepaSignPreviewLoading, setSepaSignPreviewLoading] = React.useState(false);
  const [sepaSignPreviewError, setSepaSignPreviewError] = React.useState("");
  const [sepaSignPreviewUrl, setSepaSignPreviewUrl] = React.useState("");

  const canvasRef = React.useRef(null);
  const isDrawingRef = React.useRef(false);
  const hasSignatureRef = React.useRef(false);
  const sepaCanvasRef = React.useRef(null);
  const sepaIsDrawingRef = React.useRef(false);
  const sepaHasSignatureRef = React.useRef(false);

  const replaceContractPreviewUrl = React.useCallback((nextUrl) => {
    setSignContractPreviewUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return nextUrl || "";
    });
  }, []);

  const replaceSepaPreviewUrl = React.useCallback((nextUrl) => {
    setSignSepaPreviewUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return nextUrl || "";
    });
  }, []);

  const replacePreviewContractUrl = React.useCallback((nextUrl) => {
    setPreviewContractUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return nextUrl || "";
    });
  }, []);

  const replacePreviewSepaUrl = React.useCallback((nextUrl) => {
    setPreviewSepaUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return nextUrl || "";
    });
  }, []);

  const replaceSepaSignPreviewUrl = React.useCallback((nextUrl) => {
    setSepaSignPreviewUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return nextUrl || "";
    });
  }, []);

  const resetSignatureDialog = React.useCallback(() => {
    setSignDialogOpen(false);
    setSignTargetRow(null);
    setSignReload(null);
    setAcceptedTerms(false);
    setSignSepa(true);
    setSignError("");
    setSignSubmitting(false);
    setSignPreviewLoading(false);
    setSignPreviewError("");
    replaceContractPreviewUrl("");
    replaceSepaPreviewUrl("");
    setSignAdditionalPreviewDocs((prevDocs) => {
      prevDocs.forEach((item) => {
        if (item?.url) {
          window.URL.revokeObjectURL(item.url);
        }
      });
      return [];
    });
    setSignPreviewType("contract");
    isDrawingRef.current = false;
    hasSignatureRef.current = false;
  }, [replaceContractPreviewUrl, replaceSepaPreviewUrl]);

  const resetPreviewDialog = React.useCallback(() => {
    setPreviewDialogOpen(false);
    setPreviewTargetRow(null);
    setPreviewLoading(false);
    setPreviewError("");
    replacePreviewContractUrl("");
    replacePreviewSepaUrl("");
    setPreviewAdditionalPreviewDocs((prevDocs) => {
      prevDocs.forEach((item) => {
        if (item?.url) {
          window.URL.revokeObjectURL(item.url);
        }
      });
      return [];
    });
    setPreviewType("contract");
  }, [replacePreviewContractUrl, replacePreviewSepaUrl]);

  const resetSepaDialog = React.useCallback(() => {
    setSepaDialogOpen(false);
    setSepaDialogMode("setup");
    setSepaTargetRow(null);
    setSepaReload(null);
    setSepaSubmitting(false);
    setSepaError("");
    setSepaForm({
      iban: "",
      account_holder_name: "",
      email: "",
      reason: "",
    });
  }, []);

  const resetSepaSignDialog = React.useCallback(() => {
    setSepaSignDialogOpen(false);
    setSepaSignTargetRow(null);
    setSepaSignReload(null);
    setSepaSignAcceptedTerms(false);
    setSepaSignError("");
    setSepaSignSubmitting(false);
    setSepaSignPreviewLoading(false);
    setSepaSignPreviewError("");
    replaceSepaSignPreviewUrl("");
    sepaIsDrawingRef.current = false;
    sepaHasSignatureRef.current = false;
  }, [replaceSepaSignPreviewUrl]);

  const clearSignatureCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f2740";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    hasSignatureRef.current = false;
  }, []);

  const prepareCanvas = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 900;
    canvas.height = 220;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f2740";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    hasSignatureRef.current = false;
  }, []);

  const clearSepaSignatureCanvas = React.useCallback(() => {
    const canvas = sepaCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f2740";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    sepaHasSignatureRef.current = false;
  }, []);

  const prepareSepaCanvas = React.useCallback(() => {
    const canvas = sepaCanvasRef.current;
    if (!canvas) return;
    canvas.width = 900;
    canvas.height = 220;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0f2740";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    sepaHasSignatureRef.current = false;
  }, []);

  React.useEffect(() => {
    if (!signDialogOpen) return;
    prepareCanvas();
  }, [signDialogOpen, prepareCanvas]);

  React.useEffect(() => {
    if (!sepaSignDialogOpen) return;
    prepareSepaCanvas();
  }, [sepaSignDialogOpen, prepareSepaCanvas]);

  React.useEffect(() => {
    if (!signDialogOpen || !signTargetRow?.id) {
      return undefined;
    }

    let cancelled = false;

    const getPreviewBlobUrl = async (documentId) => {
      const response = await callBackendApi(
        `/contract-documents/${documentId}/view`,
        "GET",
        null,
        false,
        { responseType: "blob" }
      );

      if (!response?.success) {
        return null;
      }

      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    };

    const pickLatestByType = (docs, type) => {
      return (docs || [])
        .filter((doc) => doc?.document_type === type)
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        })[0] || null;
    };

    const loadPreviews = async () => {
      setSignPreviewLoading(true);
      setSignPreviewError("");
      replaceContractPreviewUrl("");
      replaceSepaPreviewUrl("");
      setSignAdditionalPreviewDocs((prevDocs) => {
        prevDocs.forEach((item) => {
          if (item?.url) {
            window.URL.revokeObjectURL(item.url);
          }
        });
        return [];
      });

      const docsResponse = await callBackendApi(`/contract-documents?contract_id=${signTargetRow.id}`, "GET");
      if (!docsResponse?.success) {
        if (!cancelled) {
          setSignPreviewLoading(false);
          setSignPreviewError("Não foi possível carregar os documentos do contrato.");
        }
        return;
      }

      const docs = Array.isArray(docsResponse.data) ? docsResponse.data : [];
      const contractDoc = pickLatestByType(docs, "contract_terms");
      const sepaDoc = pickLatestByType(docs, "sepa_mandate");
      const otherDocs = (docs || [])
        .filter((doc) => doc?.document_type === "other")
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        });

      const [contractUrl, sepaUrl, ...otherUrls] = await Promise.all([
        contractDoc?.id ? getPreviewBlobUrl(contractDoc.id) : Promise.resolve(null),
        sepaDoc?.id ? getPreviewBlobUrl(sepaDoc.id) : Promise.resolve(null),
        ...otherDocs.map((doc) => (doc?.id ? getPreviewBlobUrl(doc.id) : Promise.resolve(null))),
      ]);

      if (cancelled) {
        if (contractUrl) window.URL.revokeObjectURL(contractUrl);
        if (sepaUrl) window.URL.revokeObjectURL(sepaUrl);
        return;
      }

      replaceContractPreviewUrl(contractUrl || "");
      replaceSepaPreviewUrl(sepaUrl || "");
      const additionalDocs = otherDocs
        .map((doc, idx) => ({
          id: doc?.id,
          label: doc?.file_name || doc?.notes || `Document ${idx + 1}`,
          url: otherUrls[idx] || "",
        }))
        .filter((item) => item.url);
      setSignAdditionalPreviewDocs(additionalDocs);

      setSignPreviewType(
        contractUrl
          ? "contract"
          : (sepaUrl ? "sepa" : (additionalDocs.length > 0 ? "other-0" : "contract"))
      );

      if (!contractUrl && !sepaUrl && additionalDocs.length === 0) {
        setSignPreviewError("Este contrato ainda não tem PDF para visualização.");
      }

      setSignPreviewLoading(false);
    };

    loadPreviews();

    return () => {
      cancelled = true;
    };
  }, [signDialogOpen, signTargetRow, replaceContractPreviewUrl, replaceSepaPreviewUrl]);

  React.useEffect(() => {
    if (!sepaSignDialogOpen || !sepaSignTargetRow?.id) {
      return undefined;
    }

    let cancelled = false;

    const getPreviewBlobUrl = async (documentId) => {
      const response = await callBackendApi(
        `/contract-documents/${documentId}/view`,
        "GET",
        null,
        false,
        { responseType: "blob" }
      );

      if (!response?.success) {
        return null;
      }

      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    };

    const pickLatestByType = (docs, type) => {
      return (docs || [])
        .filter((doc) => doc?.document_type === type)
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        })[0] || null;
    };

    const loadSepaPreview = async () => {
      setSepaSignPreviewLoading(true);
      setSepaSignPreviewError("");
      replaceSepaSignPreviewUrl("");

      let documentId = sepaSignTargetRow?.document_id || null;
      const contractId = sepaSignTargetRow?.contract_id || null;

      if (!documentId && contractId) {
        const docsResponse = await callBackendApi(`/contract-documents?contract_id=${contractId}`, "GET");
        if (!docsResponse?.success) {
          if (!cancelled) {
            setSepaSignPreviewLoading(false);
            setSepaSignPreviewError("Não foi possível carregar o mandato SEPA.");
          }
          return;
        }
        const docs = Array.isArray(docsResponse.data) ? docsResponse.data : [];
        documentId = pickLatestByType(docs, "sepa_mandate")?.id || null;
      }

      if (!documentId) {
        if (!cancelled) {
          setSepaSignPreviewLoading(false);
          setSepaSignPreviewError("Este mandato ainda não tem PDF para assinatura.");
        }
        return;
      }

      const sepaUrl = await getPreviewBlobUrl(documentId);
      if (cancelled) {
        if (sepaUrl) window.URL.revokeObjectURL(sepaUrl);
        return;
      }

      replaceSepaSignPreviewUrl(sepaUrl || "");
      if (!sepaUrl) {
        setSepaSignPreviewError("Não foi possível abrir o PDF do mandato.");
      }
      setSepaSignPreviewLoading(false);
    };

    loadSepaPreview();

    return () => {
      cancelled = true;
    };
  }, [sepaSignDialogOpen, sepaSignTargetRow, replaceSepaSignPreviewUrl]);

  React.useEffect(() => {
    if (!previewDialogOpen || !previewTargetRow?.id) {
      return undefined;
    }

    let cancelled = false;

    const getPreviewBlobUrl = async (documentId) => {
      const response = await callBackendApi(
        `/contract-documents/${documentId}/view`,
        "GET",
        null,
        false,
        { responseType: "blob" }
      );

      if (!response?.success) {
        return null;
      }

      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      return window.URL.createObjectURL(blob);
    };

    const pickLatestByType = (docs, type) => {
      return (docs || [])
        .filter((doc) => doc?.document_type === type)
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        })[0] || null;
    };

    const loadPreviews = async () => {
      setPreviewLoading(true);
      setPreviewError("");
      replacePreviewContractUrl("");
      replacePreviewSepaUrl("");
      setPreviewAdditionalPreviewDocs((prevDocs) => {
        prevDocs.forEach((item) => {
          if (item?.url) {
            window.URL.revokeObjectURL(item.url);
          }
        });
        return [];
      });

      const docsResponse = await callBackendApi(`/contract-documents?contract_id=${previewTargetRow.id}`, "GET");
      if (!docsResponse?.success) {
        if (!cancelled) {
          setPreviewLoading(false);
          setPreviewError("Não foi possível carregar os documentos do contrato.");
        }
        return;
      }

      const docs = Array.isArray(docsResponse.data) ? docsResponse.data : [];
      const contractDoc = pickLatestByType(docs, "contract_terms");
      const sepaDoc = pickLatestByType(docs, "sepa_mandate");
      const otherDocs = (docs || [])
        .filter((doc) => doc?.document_type === "other")
        .sort((a, b) => {
          const versionDiff = Number(b?.version || 0) - Number(a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
        });

      const [contractUrl, sepaUrl, ...otherUrls] = await Promise.all([
        contractDoc?.id ? getPreviewBlobUrl(contractDoc.id) : Promise.resolve(null),
        sepaDoc?.id ? getPreviewBlobUrl(sepaDoc.id) : Promise.resolve(null),
        ...otherDocs.map((doc) => (doc?.id ? getPreviewBlobUrl(doc.id) : Promise.resolve(null))),
      ]);

      if (cancelled) {
        if (contractUrl) window.URL.revokeObjectURL(contractUrl);
        if (sepaUrl) window.URL.revokeObjectURL(sepaUrl);
        otherUrls.forEach((url) => {
          if (url) window.URL.revokeObjectURL(url);
        });
        return;
      }

      replacePreviewContractUrl(contractUrl || "");
      replacePreviewSepaUrl(sepaUrl || "");
      const additionalDocs = otherDocs
        .map((doc, idx) => ({
          id: doc?.id,
          label: doc?.file_name || doc?.notes || `Document ${idx + 1}`,
          url: otherUrls[idx] || "",
        }))
        .filter((item) => item.url);
      setPreviewAdditionalPreviewDocs(additionalDocs);
      setPreviewType(
        contractUrl
          ? "contract"
          : (sepaUrl ? "sepa" : (additionalDocs.length > 0 ? "other-0" : "contract"))
      );

      if (!contractUrl && !sepaUrl && additionalDocs.length === 0) {
        setPreviewError("Este contrato ainda não tem PDF para visualização.");
      }

      setPreviewLoading(false);
    };

    loadPreviews();

    return () => {
      cancelled = true;
    };
  }, [previewDialogOpen, previewTargetRow, replacePreviewContractUrl, replacePreviewSepaUrl]);

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const beginDraw = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const point = getCanvasPoint(event);
    isDrawingRef.current = true;
    hasSignatureRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const drawMove = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const point = getCanvasPoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const endDraw = () => {
    isDrawingRef.current = false;
  };

  const getSepaCanvasPoint = (event) => {
    const canvas = sepaCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  };

  const beginSepaDraw = (event) => {
    const canvas = sepaCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const point = getSepaCanvasPoint(event);
    sepaIsDrawingRef.current = true;
    sepaHasSignatureRef.current = true;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const drawSepaMove = (event) => {
    if (!sepaIsDrawingRef.current) return;
    const canvas = sepaCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const point = getSepaCanvasPoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const endSepaDraw = () => {
    sepaIsDrawingRef.current = false;
  };

  const exportSignatureAsJpeg = React.useCallback((canvas) => {
    if (!canvas) return "";
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const exportCtx = exportCanvas.getContext("2d");
    if (!exportCtx) return "";
    exportCtx.fillStyle = "#ffffff";
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportCtx.drawImage(canvas, 0, 0);
    return exportCanvas.toDataURL("image/jpeg", 0.95);
  }, []);

  const openSignLocalDialog = React.useCallback(({ row, reload }) => {
    setSignTargetRow(row || null);
    setSignReload(() => reload || null);
    setSignDialogOpen(true);
    setAcceptedTerms(false);
    setSignSepa(true);
    setSignError("");
  }, []);

  const openPreviewDialog = React.useCallback(({ row }) => {
    setPreviewTargetRow(row || null);
    setPreviewDialogOpen(true);
    setPreviewError("");
  }, []);

  const openSignSepaDialog = React.useCallback(({ row, reload }) => {
    setSepaSignTargetRow(row || null);
    setSepaSignReload(() => reload || null);
    setSepaSignAcceptedTerms(false);
    setSepaSignError("");
    setSepaSignDialogOpen(true);
  }, []);

  const submitLocalSignature = async () => {
    if (!signTargetRow?.id) {
      setSignError("Contrato inválido.");
      return;
    }
    if (!acceptedTerms) {
      setSignError("Deve aceitar os termos para assinar.");
      return;
    }
    if (!hasSignatureRef.current) {
      setSignError("A assinatura é obrigatória.");
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      setSignError("Não foi possível capturar a assinatura.");
      return;
    }

    setSignSubmitting(true);
    setSignError("");

    const signatureDataUrl = exportSignatureAsJpeg(canvas);
    if (!signatureDataUrl) {
      setSignSubmitting(false);
      setSignError("Não foi possível exportar a assinatura.");
      return;
    }
    const response = await callBackendApi(`/contracts/${signTargetRow.id}/sign-local`, "POST", {
      accepted_terms: true,
      sign_sepa: signSepa,
      signature_data_url: signatureDataUrl,
      device_info: navigator?.userAgent || "",
    });

    setSignSubmitting(false);

    if (!response?.success) {
      setSignError(response?.error || "Falha ao assinar localmente.");
      return;
    }

    signReload?.();
    resetSignatureDialog();
  };

  const submitSepaSignature = async () => {
    if (!sepaSignTargetRow?.id) {
      setSepaSignError("Mandato inválido.");
      return;
    }
    if (!sepaSignAcceptedTerms) {
      setSepaSignError("Deve aceitar os termos para assinar.");
      return;
    }
    if (!sepaHasSignatureRef.current) {
      setSepaSignError("A assinatura é obrigatória.");
      return;
    }

    const canvas = sepaCanvasRef.current;
    if (!canvas) {
      setSepaSignError("Não foi possível capturar a assinatura.");
      return;
    }

    setSepaSignSubmitting(true);
    setSepaSignError("");

    const signatureDataUrl = exportSignatureAsJpeg(canvas);
    if (!signatureDataUrl) {
      setSepaSignSubmitting(false);
      setSepaSignError("Não foi possível exportar a assinatura.");
      return;
    }

    const response = await callBackendApi(`/sepa-mandates/${sepaSignTargetRow.id}/sign-local`, "POST", {
      accepted_terms: true,
      signature_data_url: signatureDataUrl,
      device_info: navigator?.userAgent || "",
    });

    setSepaSignSubmitting(false);

    if (!response?.success) {
      setSepaSignError(response?.error || "Falha ao assinar mandato SEPA.");
      return;
    }

    sepaSignReload?.();
    resetSepaSignDialog();
  };

  const getResponseErrorMessage = React.useCallback((response, fallbackMessage) => {
    const validationErrors = response?.validationErrors;
    if (validationErrors && typeof validationErrors === "object") {
      const firstKey = Object.keys(validationErrors)[0];
      const firstError = firstKey ? validationErrors[firstKey] : null;
      if (Array.isArray(firstError) && typeof firstError[0] === "string" && firstError[0].trim() !== "") {
        return firstError[0];
      }
      if (typeof firstError === "string" && firstError.trim() !== "") {
        return firstError;
      }
    }

    return response?.error || fallbackMessage;
  }, []);

  const setupSepaMandate = React.useCallback(({ row, reload }) => {
    const contractId = row?.contract_id || row?.id;
    if (!contractId) return;

    if (
      Object.prototype.hasOwnProperty.call(row || {}, "signature_date")
      && !row?.signature_date
    ) {
      window.alert("Contract must be digitally signed before SEPA setup.");
      return;
    }

    setSepaDialogMode("setup");
    setSepaTargetRow(row || null);
    setSepaReload(() => reload || null);
    setSepaForm({
      iban: (typeof row?.iban === "string" && row.iban.trim() && row.iban !== "N/A") ? row.iban : "",
      account_holder_name: row?.account_holder_name || row?.payer?.name || row?.user?.name || "",
      email: row?.payer?.email || row?.user?.email || "",
      reason: "",
    });
    setSepaError("");
    setSepaDialogOpen(true);
  }, []);

  const revokeSepaMandate = React.useCallback(({ row, reload }) => {
    const contractId = row?.contract_id || row?.id;
    if (!contractId) return;

    setSepaDialogMode("revoke");
    setSepaTargetRow(row || null);
    setSepaReload(() => reload || null);
    setSepaForm({
      iban: "",
      account_holder_name: row?.account_holder_name || row?.payer?.name || row?.user?.name || "",
      email: row?.payer?.email || row?.user?.email || "",
      reason: "",
    });
    setSepaError("");
    setSepaDialogOpen(true);
  }, []);

  const updateSepaFormField = React.useCallback((field, value) => {
    setSepaForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const submitSepaDialog = React.useCallback(async () => {
    const contractId = sepaTargetRow?.contract_id || sepaTargetRow?.id;
    if (!contractId) {
      setSepaError("Contrato inválido.");
      return;
    }

    const isMandateRow = Boolean(sepaTargetRow?.contract_id && sepaTargetRow?.mandate_reference);

    if (sepaDialogMode === "setup") {
      if (!sepaForm.iban?.trim()) {
        setSepaError("O IBAN é obrigatório.");
        return;
      }

      if (!sepaForm.account_holder_name?.trim()) {
        setSepaError("O nome do titular é obrigatório.");
        return;
      }
    }

    setSepaSubmitting(true);
    setSepaError("");

    const endpoint = sepaDialogMode === "setup"
      ? `/contracts/${contractId}/sepa/setup-intent`
      : `/contracts/${contractId}/sepa/revoke`;

    const payload = sepaDialogMode === "setup"
      ? {
          iban: sepaForm.iban.trim(),
          account_holder_name: sepaForm.account_holder_name.trim(),
          email: sepaForm.email.trim(),
          ...(isMandateRow ? {
            sepa_mandate_id: sepaTargetRow?.id,
            mandate_reference: sepaTargetRow?.mandate_reference,
          } : {}),
        }
      : {
          reason: sepaForm.reason.trim(),
          ...(isMandateRow ? { sepa_mandate_id: sepaTargetRow?.id } : {}),
        };

    const response = await callBackendApi(endpoint, "POST", payload);
    setSepaSubmitting(false);

    if (!response?.success) {
      setSepaError(
        getResponseErrorMessage(
          response,
          sepaDialogMode === "setup" ? "Falha ao criar mandato SEPA." : "Falha ao revogar mandato SEPA."
        )
      );
      return;
    }

    sepaReload?.();
    resetSepaDialog();
  }, [getResponseErrorMessage, resetSepaDialog, sepaDialogMode, sepaForm, sepaReload, sepaTargetRow]);

  const collectInstallmentNow = React.useCallback(async ({ row, reload }) => {
    const installmentId = row?.id;
    if (!installmentId) return;
    const confirmed = window.confirm("Submeter cobrança desta parcela agora?");
    if (!confirmed) return;

    const response = await callBackendApi(`/contracts/installments/${installmentId}/collect`, "POST");
    if (!response?.success) {
      window.alert(response?.error || "Falha ao submeter cobrança.");
      return;
    }

    window.alert("Cobrança submetida.");
    reload?.();
  }, []);

  const allResources = React.useMemo(
    () => createContractResources(
      openSignLocalDialog,
      openPreviewDialog,
      openSignSepaDialog,
      setupSepaMandate,
      revokeSepaMandate,
      collectInstallmentNow
    ),
    [openSignLocalDialog, openPreviewDialog, openSignSepaDialog, setupSepaMandate, revokeSepaMandate, collectInstallmentNow]
  );

  const resources = React.useMemo(
    () => allResources.filter((resource) => ["contract-plans", "contract-plan-versions", "contracts"].includes(resource.key)),
    [allResources]
  );

  const resourceLookup = React.useMemo(
    () => Object.fromEntries(allResources.map((resource) => [resource.key, resource])),
    [allResources]
  );

  return (
    <>
      <SectionCrudPage
        title="Contracts"
        subtitle="Manage contract plans, generated contracts, installments, debts, documents and signatures."
        resources={resources}
        resourceLookup={resourceLookup}
      />

      <LocalContractSignDialog
        open={signDialogOpen}
        onClose={resetSignatureDialog}
        contractLabel={signTargetRow?.contract_number || signTargetRow?.id}
        loading={signPreviewLoading}
        error={signError}
        previewError={signPreviewError}
        previewType={signPreviewType}
        onPreviewTypeChange={setSignPreviewType}
        contractUrl={signContractPreviewUrl}
        sepaUrl={signSepaPreviewUrl}
        additionalDocs={signAdditionalPreviewDocs}
        canvasRef={canvasRef}
        onBeginDraw={beginDraw}
        onDrawMove={drawMove}
        onEndDraw={endDraw}
        onClearSignature={clearSignatureCanvas}
        acceptedTerms={acceptedTerms}
        onAcceptedTermsChange={setAcceptedTerms}
        signSepa={signSepa}
        onSignSepaChange={setSignSepa}
        submitting={signSubmitting}
        onSubmit={submitLocalSignature}
      />

      <SepaMandateSignDialog
        open={sepaSignDialogOpen}
        onClose={resetSepaSignDialog}
        mandateLabel={sepaSignTargetRow?.mandate_reference || sepaSignTargetRow?.id}
        loading={sepaSignPreviewLoading}
        error={sepaSignError}
        previewError={sepaSignPreviewError}
        sepaUrl={sepaSignPreviewUrl}
        canvasRef={sepaCanvasRef}
        onBeginDraw={beginSepaDraw}
        onDrawMove={drawSepaMove}
        onEndDraw={endSepaDraw}
        onClearSignature={clearSepaSignatureCanvas}
        acceptedTerms={sepaSignAcceptedTerms}
        onAcceptedTermsChange={setSepaSignAcceptedTerms}
        submitting={sepaSignSubmitting}
        onSubmit={submitSepaSignature}
      />

      <ContractDocumentsPreviewDialog
        open={previewDialogOpen}
        onClose={resetPreviewDialog}
        contractLabel={previewTargetRow?.contract_number || previewTargetRow?.id}
        loading={previewLoading}
        error={previewError}
        previewType={previewType}
        onPreviewTypeChange={setPreviewType}
        contractUrl={previewContractUrl}
        sepaUrl={previewSepaUrl}
        additionalDocs={previewAdditionalPreviewDocs}
      />

      <SepaMandateDialog
        open={sepaDialogOpen}
        mode={sepaDialogMode}
        contractLabel={sepaTargetRow?.contract_number || sepaTargetRow?.id}
        form={sepaForm}
        onChange={updateSepaFormField}
        submitting={sepaSubmitting}
        error={sepaError}
        onClose={resetSepaDialog}
        onSubmit={submitSepaDialog}
      />
    </>
  );
}
